// Phase 215 — Bill Vendors master list. SEPARATE from /vendors (which
// handles building-management vendors like elevator/insurance/maintenance
// companies). This page manages utility/service providers used by the
// Bills module (EAC, CYTA, water board, municipality, suppliers, etc.).
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, MagnifyingGlass, PencilSimple, Trash, ArrowCounterClockwise, ArrowLeft, Receipt } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BillVendors() {
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState('');
  const [archiveFilter, setArchiveFilter] = useState('active');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [deletingVendor, setDeletingVendor] = useState(null);
  const [form, setForm] = useState({
    name: '', category: '', contact_person: '', phone: '', email: '', account_number: '', notes: ''
  });
  // Phase 226 — Bills-only categories source. Filters out legacy_* synthetic
  // entries returned by GET /bills/categories so the dropdown only shows
  // categories the admin has explicitly managed.
  const [billCategories, setBillCategories] = useState([]);

  const fetchBillCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bills/categories`);
      const list = (res.data || []).filter(c => !String(c.category_id || '').startsWith('legacy_'));
      setBillCategories(list);
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { fetchBillCategories(); }, [fetchBillCategories]);

  // The summary endpoint is what gives us per-vendor totals
  // (invoice_count, total_unpaid, overdue_count) — we prefer it over the
  // bare list when in "active" view since active vendors usually have
  // bills attached.
  const fetchVendors = useCallback(async (af) => {
    try {
      const filter = af || archiveFilter;
      if (filter === 'active') {
        const res = await axios.get(`${API}/bills/vendors-summary`);
        setVendors(res.data || []);
      } else {
        const res = await axios.get(`${API}/bill-vendors?archive_filter=${filter}`);
        setVendors((res.data || []).map(v => ({ ...v, invoice_count: 0, total_unpaid: 0, overdue_count: 0 })));
      }
    } catch (err) { console.error(err); }
  }, [archiveFilter]);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const handleArchiveFilter = (val) => { setArchiveFilter(val); fetchVendors(val); };

  const filtered = vendors.filter(v =>
    (v.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (v.category || '').toLowerCase().includes(search.toLowerCase()) ||
    (v.phone || '').includes(search) ||
    (v.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (v.account_number || '').toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setEditingVendor(null);
    setForm({ name: '', category: '', contact_person: '', phone: '', email: '', account_number: '', notes: '' });
    setDialogOpen(true);
  };

  const openEdit = (v) => {
    setEditingVendor(v);
    setForm({
      name: v.name || '',
      category: v.category || '',
      contact_person: v.contact_person || '',
      phone: v.phone || '',
      email: v.email || '',
      account_number: v.account_number || '',
      notes: v.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingVendor) {
        await axios.put(`${API}/bill-vendors/${editingVendor.vendor_id}`, form);
      } else {
        await axios.post(`${API}/bill-vendors`, form);
      }
      toast.success(t('savedSuccess'));
      setDialogOpen(false);
      fetchVendors();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  const handleDelete = async () => {
    if (!deletingVendor) return;
    try {
      const res = await axios.delete(`${API}/bill-vendors/${deletingVendor.vendor_id}`);
      toast.success(res.data?.archived ? (t('vendorArchived') || 'Vendor archived') : t('deleteSuccess'));
      setDeleteDialogOpen(false);
      setDeletingVendor(null);
      fetchVendors();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/bills')} className="p-2 hover:bg-zinc-100 transition-colors" data-testid="back-to-bills">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-zinc-900" data-testid="bill-vendors-title">
                {t('billVendors') || 'Bills'}
              </h1>
              <p className="text-xs text-zinc-500 mt-0.5">
                {t('billVendorsDesc') || 'Manage your bills (utility & service invoices). Independent from Building Management vendors.'}
              </p>
            </div>
          </div>
          {isAdmin && (
            <Button onClick={openCreate} className="bg-zinc-950 rounded-none" data-testid="add-bill-vendor-btn">
              <Plus size={16} className="mr-2" />{t('addBillEntry') || 'Add Bill'}
            </Button>
          )}
        </div>

        {/* Search + Archive Filter */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('searchBills') || 'Search bills...'}
              className="pl-9 rounded-none"
              data-testid="bill-vendor-search"
            />
          </div>
          <select
            value={archiveFilter}
            onChange={(e) => handleArchiveFilter(e.target.value)}
            className="text-sm border border-zinc-200 rounded-none px-3 py-2 bg-white text-zinc-700 focus:outline-none"
            data-testid="bill-vendor-archive-filter"
          >
            <option value="active">{t('activeItems') || 'Active'}</option>
            <option value="archived">{t('archivedItems') || 'Archived'}</option>
          </select>
        </div>

        {/* Vendor List */}
        {filtered.length === 0 ? (
          <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500" data-testid="bill-vendor-list">
            {t('noBills') || 'No bills found.'}
          </div>
        ) : (
          <div className="bg-white border border-zinc-200 overflow-hidden" data-testid="bill-vendor-list">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                    <th className="text-left px-4 py-2.5">{t('billEntryName') || 'Name'}</th>
                    <th className="text-left px-4 py-2.5">{t('category') || 'Category'}</th>
                    <th className="text-left px-4 py-2.5">{t('contactPerson') || 'Contact'}</th>
                    <th className="text-left px-4 py-2.5">{t('phone') || 'Phone'}</th>
                    <th className="text-left px-4 py-2.5 font-mono">{t('accountNumber') || 'Account #'}</th>
                    <th className="text-right px-4 py-2.5">{t('invoices') || 'Invoices'}</th>
                    <th className="text-right px-4 py-2.5">{t('unpaid') || 'Unpaid'}</th>
                    {isAdmin && <th className="text-right px-4 py-2.5 w-28">{t('actions') || 'Actions'}</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {filtered.map(v => (
                    <tr
                      key={v.vendor_id}
                      onClick={() => navigate(`/bill-vendors/${v.vendor_id}`)}
                      className={`hover:bg-zinc-50 transition-colors cursor-pointer ${v.is_deleted ? 'opacity-60 bg-zinc-50/50' : ''}`}
                      data-testid={`bill-vendor-row-${v.vendor_id}`}
                    >
                      <td className="px-4 py-2.5">
                        <span className="font-semibold text-zinc-900 hover:text-blue-700 transition-colors">{v.name}</span>
                        {v.is_deleted && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold bg-zinc-200 text-zinc-600 border border-zinc-300">
                            {t('archived') || 'Archived'}
                          </span>
                        )}
                        {v.overdue_count > 0 && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold bg-red-100 text-red-700 border border-red-200">
                            {v.overdue_count} {t('overdue') || 'overdue'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-zinc-600">{v.category || '—'}</td>
                      <td className="px-4 py-2.5 text-zinc-600">{v.contact_person || '—'}</td>
                      <td className="px-4 py-2.5 text-zinc-600 font-mono text-xs">{v.phone || '—'}</td>
                      <td className="px-4 py-2.5 text-zinc-600 font-mono text-xs">{v.account_number || '—'}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{v.invoice_count ?? 0}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-semibold text-red-700">
                        €{(v.total_unpaid || 0).toFixed(2)}
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-2.5 text-right" onClick={e => e.stopPropagation()}>
                          <div className="flex justify-end gap-1">
                            {v.is_deleted ? (
                              <>
                                <button
                                  onClick={async () => {
                                    try { await axios.post(`${API}/bill-vendors/${v.vendor_id}/restore`); toast.success(t('itemRestored') || 'Restored'); fetchVendors(); }
                                    catch { toast.error(t('errorOccurred')); }
                                  }}
                                  className="p-1.5 hover:bg-emerald-50 text-emerald-600"
                                  title={t('restore')}
                                  data-testid={`restore-bill-vendor-${v.vendor_id}`}
                                >
                                  <ArrowCounterClockwise size={14} />
                                </button>
                                <button
                                  onClick={async () => {
                                    if (window.confirm(t('confirmPermanentDelete') || 'Permanently delete this vendor and ALL its bills?')) {
                                      try { await axios.delete(`${API}/bill-vendors/${v.vendor_id}/permanent`); toast.success(t('itemPermanentlyDeleted') || 'Deleted'); fetchVendors(); }
                                      catch { toast.error(t('errorOccurred')); }
                                    }
                                  }}
                                  className="p-1.5 hover:bg-red-50 text-red-600"
                                  title={t('permanentlyDelete') || 'Permanently delete'}
                                  data-testid={`perm-delete-bill-vendor-${v.vendor_id}`}
                                >
                                  <Trash size={14} />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => openEdit(v)}
                                  className="p-1.5 hover:bg-zinc-100"
                                  title={t('edit')}
                                  data-testid={`edit-bill-vendor-${v.vendor_id}`}
                                >
                                  <PencilSimple size={14} />
                                </button>
                                <button
                                  onClick={() => { setDeletingVendor(v); setDeleteDialogOpen(true); }}
                                  className="p-1.5 hover:bg-red-50 text-red-600"
                                  title={t('delete')}
                                  data-testid={`delete-bill-vendor-${v.vendor_id}`}
                                >
                                  <Trash size={14} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md rounded-none">
            <DialogHeader>
              <DialogTitle>{editingVendor ? (t('editBillEntry') || 'Edit Bill') : (t('addBillEntry') || 'Add Bill')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>{t('billEntryName') || 'Bill Name'} *</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="rounded-none mt-1" data-testid="bill-vendor-name-input" />
                </div>
                <div>
                  <Label>{t('category') || 'Category'}</Label>
                  <Select
                    value={form.category || '__none__'}
                    onValueChange={v => setForm({ ...form, category: v === '__none__' ? '' : v })}
                  >
                    <SelectTrigger className="rounded-none mt-1" data-testid="bill-vendor-category-input">
                      <SelectValue placeholder={t('selectCategory') || 'Select category…'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— {t('none') || 'None'} —</SelectItem>
                      {billCategories.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-zinc-400">{t('billNoCategoriesYet') || 'No categories yet. Add some via Manage Categories.'}</div>
                      ) : billCategories.map(c => (
                        <SelectItem key={c.category_id} value={c.name} data-testid={`bill-vendor-cat-${c.category_id}`}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('accountNumber') || 'Account Number'}</Label>
                  <Input value={form.account_number} onChange={e => setForm({ ...form, account_number: e.target.value })} className="rounded-none mt-1 font-mono" data-testid="bill-vendor-account-input" />
                </div>
                <div>
                  <Label>{t('contactPerson') || 'Contact Person'}</Label>
                  <Input value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} className="rounded-none mt-1" data-testid="bill-vendor-contact-input" />
                </div>
                <div>
                  <Label>{t('phone') || 'Phone'}</Label>
                  <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="rounded-none mt-1 font-mono" data-testid="bill-vendor-phone-input" />
                </div>
                <div className="col-span-2">
                  <Label>{t('email') || 'Email'}</Label>
                  <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="rounded-none mt-1" data-testid="bill-vendor-email-input" />
                </div>
                <div className="col-span-2">
                  <Label>{t('notes') || 'Notes'}</Label>
                  <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full border border-zinc-200 p-2 text-sm min-h-[60px] mt-1" data-testid="bill-vendor-notes-input" />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="bill-vendor-submit-btn">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete (archive) Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="rounded-none">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('confirmDelete') || 'Delete Vendor'}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('deleteVendorWarning') || 'This will archive the vendor (bills are preserved). You can restore or permanently delete from the Archive view.'}
                {' '}<strong>{deletingVendor?.name}</strong>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="confirm-delete-bill-vendor">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
